import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Settings, User, LogOut, Shield } from 'lucide-react';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
interface AppHeaderProps {
  title?: string;
}
const AppHeader: React.FC<AppHeaderProps> = ({
  title
}) => {
  const {
    user,
    signOut
  } = useAuth();
  const {
    currentUserRole
  } = useUserRoles();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isSuperAdmin } = useSuperAdmin();
  const { data: activeOrganization } = useActiveOrganization();

  // Fetch user profile to get full name
  const {
    data: profile
  } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const {
        data,
        error
      } = await supabase.from('profiles').select('full_name, first_name, last_name').eq('id', user.id).single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });
  const handleSignOut = async () => {
    await signOut();
  };
  const getUserInitials = () => {
    if (profile?.full_name) {
      return profile.full_name.split(' ').map(name => name.charAt(0)).slice(0, 2).join('').toUpperCase();
    }
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    if (!user?.email) return 'U';
    return user.email.charAt(0).toUpperCase();
  };
  const getDisplayName = () => {
    if (profile?.full_name) {
      return profile.full_name;
    }
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return user?.email || 'User';
  };
  return <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md border-b px-5 w-full py-[15px]">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center">
            <img src="https://sb-01.tabletally.org/storage/v1/object/public/tabletally-img/table-tally-logo.png" alt="TableTally Logo" className="h-10 sm:h-14 w-auto object-contain" />
          </Link>
          
          {/* Separator - hidden on mobile */}
          <Separator orientation="vertical" className="h-8 mx-2 hidden sm:block" />
          
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            {activeOrganization && <>
              <span className="font-medium text-foreground">{activeOrganization.name}</span>
              {activeOrganization.is_test && <Badge variant="secondary" className="uppercase tracking-normal">Test</Badge>}
            </>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Show individual buttons only on desktop */}
          {!isMobile && <>
              <Link to="/admin">
                <Button variant="ghost" size="icon" title="Settings">
                  <Settings className="h-5 w-5" />
                </Button>
              </Link>

              {isSuperAdmin && (
                <Link to="/super-admin">
                  <Button variant="ghost" size="icon" title="Super Admin">
                    <Shield className="h-5 w-5 text-primary" />
                  </Button>
                </Link>
              )}
            </>}

          {/* Show sidebar trigger only on mobile */}
          {isMobile && <SidebarTrigger />}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" alt="User avatar" />
                  <AvatarFallback>{getUserInitials()}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <div className="flex flex-col space-y-1 p-2">
                <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
                {currentUserRole && <p className="text-xs leading-none text-muted-foreground">
                    {currentUserRole}
                  </p>}
              </div>
              <DropdownMenuSeparator />
              
              {/* Show these menu items only on mobile */}
              {isMobile && <>
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  {isSuperAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/super-admin" className="flex items-center">
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Super Admin</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                </>}
              
              <DropdownMenuItem asChild>
                <a href="/profile" className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>;
};
export default AppHeader;